import React, { useEffect, useState } from 'react';
import {
  MessageSquare, Loader2, TrendingUp, AlertCircle, CheckCircle2, DollarSign,
  Calendar, RefreshCw, Filter
} from 'lucide-react';
import { api } from '../../services/api';

const COUNTRY_FLAGS = {
  DE: '🇩🇪', CH: '🇨🇭', AT: '🇦🇹', FR: '🇫🇷', IT: '🇮🇹',
  NL: '🇳🇱', ES: '🇪🇸', GB: '🇬🇧', BE: '🇧🇪', PT: '🇵🇹',
  EG: '🇪🇬', SA: '🇸🇦', AE: '🇦🇪', JO: '🇯🇴', MA: '🇲🇦',
  TR: '🇹🇷', US: '🇺🇸', GR: '🇬🇷',
};

const TRIGGER_LABELS = {
  verification: 'Verifizierung',
  '2fa': '2FA Login',
  new_offer: 'Neues Angebot',
  booking_confirmed_customer: 'Buchung (Kunde)',
  booking_confirmed_driver: 'Buchung (Fahrer)',
  pickup_reminder_customer: 'Erinnerung (Kunde)',
  pickup_reminder_driver: 'Erinnerung (Fahrer)',
  unknown: 'Sonstige',
};

const STATUS_COLORS = {
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  skipped: 'bg-slate-100 text-slate-600',
};

export function AdminSmsStatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (filterCountry) params.country = filterCountry;
      if (filterTrigger) params.trigger = filterTrigger;
      const res = await api.get('/admin/sms-stats', { params });
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Laden der SMS-Statistik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, filterCountry, filterTrigger]);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-slate-300" />
      </div>
    );
  }

  if (!data) return null;
  const { totals, by_country = [], by_trigger = [], by_day = [], recent = [] } = data;

  const maxDayCount = Math.max(1, ...by_day.map(d => d.count));

  return (
    <div className="space-y-5" data-testid="admin-sms-stats-tab">
      {/* Header & period selector */}
      <div className="bg-white rounded-xl border border-slate-100 p-5 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={18} className="text-orange-500" />
            SMS-Statistik (Twilio)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Übersicht aller versandten SMS und geschätzten Kosten</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="sms-stats-period"
          >
            <option value={1}>Letzten 24h</option>
            <option value={7}>Letzte 7 Tage</option>
            <option value={30}>Letzte 30 Tage</option>
            <option value={90}>Letzte 90 Tage</option>
            <option value={365}>Letztes Jahr</option>
          </select>
          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="sms-stats-country-filter"
          >
            <option value="">Alle Länder</option>
            {by_country.map(c => (
              <option key={c.country} value={c.country}>{COUNTRY_FLAGS[c.country] || ''} {c.country}</option>
            ))}
          </select>
          <select
            value={filterTrigger}
            onChange={(e) => setFilterTrigger(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="sms-stats-trigger-filter"
          >
            <option value="">Alle Trigger</option>
            {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            onClick={load}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600"
            title="Neu laden"
            data-testid="sms-stats-refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card icon={MessageSquare} iconColor="text-blue-500" bg="bg-blue-50 border-blue-100" color="text-blue-800"
              label="Gesamt SMS" value={totals.count || 0} testid="stat-total" />
        <Card icon={CheckCircle2} iconColor="text-emerald-500" bg="bg-emerald-50 border-emerald-100" color="text-emerald-800"
              label="Erfolgreich" value={totals.sent || 0} testid="stat-sent" />
        <Card icon={AlertCircle} iconColor="text-rose-500" bg="bg-rose-50 border-rose-100" color="text-rose-800"
              label="Fehlgeschlagen" value={totals.failed || 0} testid="stat-failed" />
        <Card icon={TrendingUp} iconColor="text-indigo-500" bg="bg-indigo-50 border-indigo-100" color="text-indigo-800"
              label="Segmente" value={totals.segments || 0} testid="stat-segments" />
        <Card icon={DollarSign} iconColor="text-orange-500" bg="bg-orange-50 border-orange-200" color="text-orange-800"
              label={`Geschätzte Kosten (USD ≈ ${totals.fx_rate_usd_eur?.toFixed?.(2) || '0.92'} EUR)`}
              value={`${totals.cost_usd?.toFixed?.(2) || '0.00'} USD`}
              sub={`≈ ${totals.cost_eur?.toFixed?.(2) || '0.00'} EUR`} testid="stat-cost" />
      </div>

      {/* Daily timeline */}
      {by_day.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-1.5">
            <Calendar size={14} /> Versand-Verlauf
          </h3>
          <div className="flex items-end gap-1 h-32 overflow-x-auto">
            {by_day.map(d => (
              <div key={d.date} className="flex flex-col items-center justify-end shrink-0" style={{ width: 28 }} title={`${d.date}: ${d.count} SMS (${d.sent} ok, ${d.failed} fail)`}>
                <div className="text-[9px] text-slate-400 mb-0.5">{d.count}</div>
                <div className="w-full bg-orange-500 rounded-t" style={{ height: `${(d.count / maxDayCount) * 100}%`, minHeight: 2 }}></div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-2 text-right">{by_day[0]?.date} – {by_day[by_day.length - 1]?.date}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By country */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5">
            <Filter size={14} /> Nach Land
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100">
                <th className="text-left py-2">Land</th>
                <th className="text-right">Anzahl</th>
                <th className="text-right">OK</th>
                <th className="text-right">Fail</th>
                <th className="text-right">Kosten</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {by_country.map(c => (
                <tr key={c.country} data-testid={`country-row-${c.country}`}>
                  <td className="py-2">{COUNTRY_FLAGS[c.country] || '🌍'} <span className="text-slate-700 font-medium">{c.country}</span></td>
                  <td className="text-right text-slate-700">{c.count}</td>
                  <td className="text-right text-emerald-600">{c.sent}</td>
                  <td className="text-right text-rose-600">{c.failed || 0}</td>
                  <td className="text-right font-medium text-orange-700">{c.cost_usd?.toFixed?.(2) || '0.00'} $</td>
                </tr>
              ))}
              {!by_country.length && <tr><td colSpan="5" className="py-6 text-center text-slate-400 text-xs">Keine SMS in diesem Zeitraum</td></tr>}
            </tbody>
          </table>
        </div>

        {/* By trigger */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5">
            <Filter size={14} /> Nach Trigger
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100">
                <th className="text-left py-2">Auslöser</th>
                <th className="text-right">Anzahl</th>
                <th className="text-right">Segmente</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {by_trigger.map(t => (
                <tr key={t.trigger} data-testid={`trigger-row-${t.trigger}`}>
                  <td className="py-2 text-slate-700">{TRIGGER_LABELS[t.trigger] || t.trigger}</td>
                  <td className="text-right text-slate-700 font-medium">{t.count}</td>
                  <td className="text-right text-slate-500">{t.segments}</td>
                </tr>
              ))}
              {!by_trigger.length && <tr><td colSpan="3" className="py-6 text-center text-slate-400 text-xs">Keine Daten</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent SMS */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-700">Letzte 100 SMS</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs text-slate-500">
                <th className="text-left px-4 py-2.5">Zeit</th>
                <th className="text-left px-4 py-2.5">Empfänger</th>
                <th className="text-left px-4 py-2.5">Land</th>
                <th className="text-left px-4 py-2.5">Trigger</th>
                <th className="text-center px-4 py-2.5">Segs</th>
                <th className="text-left px-4 py-2.5">Kanal</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Fehler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((r, i) => (
                <tr key={r.sid || r.created_at + i} className="hover:bg-slate-50" data-testid={`sms-row-${i}`}>
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString('de-DE')}</td>
                  <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{r.to}</td>
                  <td className="px-4 py-2.5">{COUNTRY_FLAGS[r.country] || '🌍'} {r.country}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{TRIGGER_LABELS[r.trigger] || r.trigger}</td>
                  <td className="px-4 py-2.5 text-center">{r.segments || 1}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{r.channel || '-'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[r.status] || 'bg-slate-100'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-rose-600 max-w-xs truncate">{r.error || ''}</td>
                </tr>
              ))}
              {!recent.length && <tr><td colSpan="8" className="text-center py-10 text-slate-400 text-xs">Keine SMS-Einträge</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, iconColor, bg, color, label, value, sub, testid }) {
  return (
    <div className={`rounded-xl p-4 border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className={iconColor} />
        <p className="text-xs font-medium text-slate-600 truncate">{label}</p>
      </div>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
